/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { ITable, SmartTable } from "@tedivo/tedivo-smart-table";
import { getTranslation, i18nReactive } from "../../app/i18/i18tn";

import { DivWithSpinner } from "@tedivo/tedivo-ui";
import { EditDrawer } from "./EditDrawer";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import ICustomError from "../types/ICustomError";
import { IFormReturn } from "@tedivo/tedivo-form";
import { IResponseModel } from "../../app/services/HttpClient";
import { IntegratedDialogError } from "@tedivo/tedivo-ui";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import globalStore from "../../app/stores/globalStore";
import goSquared from "../../app/tracking/goSquared";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import securityModule from "../../app/security/SecurityModule";
import topMessageElement from "../layout/top-tools/getTopMessageElement";

export class ListComponentWithActions<
  T extends Record<string, any>,
> extends HTMLElement {
  public static observedAttributes = [];

  private i18nCR: I18nComponentRegisterer;
  private dialogError: IntegratedDialogError;
  private wrapper: DivWithSpinner;
  private mainBox: HTMLDivElement;
  private deleteDialog: SlDialog;
  private editDrawer: EditDrawer;
  private internalData: T[] = [];

  public smartTable: SmartTable<T> | undefined = undefined;

  public goSquaredLabel = "";
  public h1Label = "";
  public topLabel = "";
  public createLabel = "";
  public editLabel = "";
  public deleteLabel = "";
  public addRefreshButton = false;
  public elementsForActionBox: HTMLElement[] = [];

  public systemRightToEdit = "";

  public tableConfig: ((data: T[]) => ITable<T>) | undefined = undefined;

  public serviceListCall: (() => Promise<IResponseModel<T[]>>) | undefined =
    undefined;

  public preDisplayFnCall: (() => Promise<void>) | undefined = undefined;

  public serviceDeleteCall:
    | ((pk: string, orgId?: string) => Promise<IResponseModel<unknown>>)
    | undefined = undefined;

  public serviceEditCall:
    | ((dta: T) => Promise<IResponseModel<unknown>>)
    | undefined = undefined;

  public createEditForm:
    | ((
        submitButton: SlButton,
        prevData?: T,
        allPrevData?: T[],
      ) => IFormReturn<T>)
    | undefined = undefined;

  constructor() {
    super();
    this.i18nCR = new I18nComponentRegisterer(i18nReactive);
    this.dialogError = new IntegratedDialogError(
      this,
      false,
      getTranslation("general:common.close"),
    );
    this.wrapper = document.createElement("div-spinner-element");
    this.mainBox = document.createElement("div");
    this.mainBox.className = "oss-card";
    this.deleteDialog = document.createElement("sl-dialog");
    this.editDrawer = new EditDrawer();
  }

  async connectedCallback() {
    goSquared.addEvent(`${this.goSquaredLabel} - Show page`);

    const wrapper = this.wrapper,
      h1 = document.createElement("h1");

    wrapper.appendChild(h1);
    wrapper.appendChild(this.mainBox);

    this.appendChild(this.editDrawer.drawer);
    this.appendChild(wrapper);

    if (this.preDisplayFnCall) {
      wrapper.setLoading(true);
      await this.preDisplayFnCall();
    }

    this.displayList();

    this.i18nCR.addConsumer(
      topMessageElement.element,
      this.topLabel,
      "innerHTML",
    );
    this.i18nCR.addConsumer(h1, this.h1Label, "innerHTML");
  }

  set needsUpdate(value: boolean) {
    if (value) this.displayList();
  }

  private displayList = () => {
    let addActionBox = false;

    if (!this.serviceListCall) {
      throw new Error("serviceCall is undefined");
    }

    if (this.tableConfig === undefined) {
      throw new Error("tableConfig is undefined");
    }

    globalStore.touchLastUserInteraction();

    const wrapper = this.wrapper;
    const box = this.mainBox;
    removeChildren(box);

    const actionBox = document.createElement("div");
    actionBox.className = "action-box";

    this.elementsForActionBox.forEach((el) => {
      if (el instanceof HTMLElement) {
        actionBox.appendChild(el);
        addActionBox = true;
      }
    });

    if (
      this.createLabel &&
      this.serviceEditCall &&
      securityModule.userHasPermission(this.systemRightToEdit)
    ) {
      const actionAddNewButton = document.createElement("sl-button");
      actionAddNewButton.innerHTML = getTranslation(this.createLabel);
      actionAddNewButton.variant = "primary";
      actionAddNewButton.style.order = "10";
      actionAddNewButton.addEventListener(
        "click",
        () => {
          this.openEditModal("", undefined, this.internalData);
        },
        false,
      );
      addActionBox = true;
      actionBox.appendChild(actionAddNewButton);
    }

    if (this.addRefreshButton) {
      const reloadBtnIcon = document.createElement("sl-icon");
      reloadBtnIcon.name = "arrow-clockwise";
      const reloadBtn = document.createElement("sl-button");
      reloadBtn.outline = true;
      reloadBtn.variant = "primary";
      reloadBtn.appendChild(reloadBtnIcon);
      reloadBtn.title = getTranslation("general:common.refresh");
      reloadBtn.className = "smart-table-reload-btn";
      reloadBtn.style.order = "100";
      reloadBtn.addEventListener("click", async () => {
        reloadBtn.disabled = true;
        this.displayList();
        reloadBtn.disabled = false;
      });
      addActionBox = true;
      actionBox.appendChild(reloadBtn);
    }

    wrapper.setLoading(true);

    this.serviceListCall()
      .then((resp) => {
        if (resp.data) {
          const tableConfig: ITable<T> = this.tableConfig!(resp.data);
          this.internalData = resp.data;

          if (tableConfig.settings.actionBox) {
            addActionBox = true;
            actionBox.appendChild(tableConfig.settings.actionBox);
          }

          const tbl = new SmartTable<T>().initialize({
            ...tableConfig,
            settings: {
              ...tableConfig.settings,
              createSortIconFn: createSortIcon,
              actionBox: addActionBox ? actionBox : undefined,
            },
          });

          this.smartTable = tbl;

          box.appendChild(tbl);
        }
      })
      .catch((e) => {
        this.showError(e);
      })
      .finally(() => {
        wrapper.setLoading(false);
      });
  };

  disconnectedCallback() {
    this.i18nCR.disconnect();
  }

  showError = (e: ICustomError) => {
    this.dialogError.show(
      getTranslation(`errors:errorHasOcurred`),
      getTranslation(typeof e === "string" ? e : e.translationKey),
    );
    console.error(e);
  };

  openEditModal = (pk?: string, prevData?: T, allPrevData?: T[]) => {
    if (!this.serviceEditCall) {
      throw new Error("serviceEditCall is undefined");
    }

    if (!this.createEditForm) {
      throw new Error("createEditForm is undefined");
    }

    const editForm = this.createEditForm(
      this.editDrawer.submitButton,
      prevData,
      allPrevData,
    );

    if (editForm) {
      const drawer = this.editDrawer.getEditDrawer({
        title: getTranslation(this.editLabel || this.createLabel),
        showUnits: false,
        readOnlyMode: false,
        onSave: async () => {
          this.editDrawer.disabled = true;
          const valid = await editForm.submitFunction();
          this.editDrawer.disabled = false;

          if (valid) {
            this.displayList();
          }

          return valid;
        },
      });
      drawer.appendChild(editForm.node);
      drawer.show();
    }
  };

  openDeleteModal = (
    pk: string,
    dta: T,
    name: keyof T,
    secondKey?: keyof T,
  ) => {
    const dialog = this.deleteDialog;
    this.appendChild(dialog);

    const htmlContent = document.createElement("div");
    htmlContent.innerHTML = `<strong>${dta[name]}</strong>`;

    showDeleteModal({
      dialog,
      htmlContent,
      deleteLabel: this.deleteLabel,
      serviceDeleteCall: async () => {
        const resp = await this.serviceDeleteCall!(
          pk,
          secondKey ? dta[secondKey] : undefined,
        );
        this.displayList();
        return resp;
      },
    });
  };
}

export function createSortIcon(asc: boolean) {
  const icon = document.createElement("sl-icon");
  icon.name = asc ? "sort-down-alt" : "sort-up-alt";
  return icon;
}

export function showDeleteModal({
  dialog,
  htmlContent,
  deleteLabel,
  serviceDeleteCall,
}: {
  dialog: SlDialog;
  htmlContent: HTMLDivElement;
  deleteLabel: string;
  serviceDeleteCall: () => Promise<IResponseModel<unknown>>;
}) {
  if (!serviceDeleteCall) {
    throw new Error("serviceDeleteCall is undefined");
  }

  removeChildren(dialog);

  dialog.label = getTranslation(deleteLabel);

  const closeBtn = document.createElement("sl-button");
  closeBtn.slot = "footer";
  closeBtn.variant = "danger";
  closeBtn.autofocus = true;
  closeBtn.tabIndex = 0;
  closeBtn.innerHTML = getTranslation("general:common.delete");
  closeBtn.addEventListener(
    "click",
    async () => {
      closeBtn.loading = true;
      closeBtn.disabled = true;
      await serviceDeleteCall();
      dialog.hide();
    },
    false,
  );

  dialog.appendChild(htmlContent);
  dialog.appendChild(closeBtn);

  dialog.show();
}
